import type { DateQuestionRules, DateRuleBound, DateTimeQuestionConfig, FormField } from '../types';
import { formatNameAnswer, normalizeFullName } from './name-answers';

/** Shared publication and runtime bound for authored respondent instructions. */
export const MAX_QUESTION_HELP_TEXT_LENGTH = 10_000;

/** Uploaded workspace image referenced by a visual question. */
export interface QuestionImage {
  /** Public workspace-asset route rendered by the respondent client. */
  imageUrl: string;
  /** Private workspace object key used for lifecycle management. */
  imageStorageKey: string;
  /** Required respondent-facing alternative text. */
  alt: string;
  /** Optional private visual surrogate used by model-backed authoring. */
  caption?: string;
}

/** One image-space coordinate normalized independently of rendered dimensions. */
export interface NormalizedPoint {
  x: number;
  y: number;
}

/** Local ISO value collected by a date/time question. */
export interface DateTimeAnswerValue {
  value: string;
  /** Required for combined local date-time values so the instant is unambiguous. */
  timezone?: string;
}

/** Configured column ids per row; legacy single selections remain scalar strings. */
export interface MatrixAnswerValue {
  selections: Record<string, string | string[]>;
}

/** Structured postal address retained without lossy string encoding. */
export interface AddressAnswerValue {
  line1: string;
  line2?: string;
  city: string;
  region?: string;
  postalCode?: string;
  country: string;
}

/** One compact signature coordinate normalized to the inclusive 0..10000 range. */
export type SignaturePoint = [number, number];

/** Legacy signature values retained for read compatibility. */
export type LegacySignatureAnswerValue =
  | { kind: 'typed'; typedName: string }
  | { kind: 'drawn'; strokes: Array<{ points: NormalizedPoint[] }> };

/** Shared geometry for a compact normalized drawn signature. */
interface DrawnSignatureGeometry {
  version: 2;
  kind: 'drawn';
  encoding: 'normalized-10000';
  canvas: { width: number; height: number };
  strokes: SignaturePoint[][];
}

/** A new signature answer with the respondent's required written name. */
export type SignatureAnswerValue =
  | { version: 2; kind: 'typed'; typedName: string }
  | (DrawnSignatureGeometry & { signerName: string });

/** Read compatibility for drawings saved before a written name was required. */
export type ReadableSignatureAnswerValue =
  | SignatureAnswerValue
  | (DrawnSignatureGeometry & { signerName?: undefined });

/** Normalized respondent clicks on a snapshotted question image. */
export interface ImageClickMapAnswerValue {
  points: NormalizedPoint[];
}

/** Structured control values accepted by the expanded question blocks. */
export type QuestionStructuredValue =
  | boolean
  | string[]
  | DateTimeAnswerValue
  | MatrixAnswerValue
  | AddressAnswerValue
  | ReadableSignatureAnswerValue
  | ImageClickMapAnswerValue;

/** Result returned by the shared structured-answer validator. */
export type QuestionAnswerValidation =
  | { valid: true; value: QuestionStructuredValue }
  | { valid: false };

/** Machine-readable question image publication issues. */
export type QuestionImageIssue = 'missing_image' | 'missing_alt' | 'invalid_image_url' | 'invalid_image_metadata';

const ON_SCREEN_TYPES = new Set([
  'checkbox',
  'image_choice',
  'ranking',
  'date_time',
  'matrix',
  'address',
  'signature',
  'image_click_map',
]);

const MAX_TEXT_LENGTH = 10_000;
const MAX_SIGNATURE_STROKES = 64;
const MAX_SIGNATURE_POINTS = 4_096;
const MAX_SIGNATURE_BYTES = 65_536;
const MAX_SIGNATURE_CANVAS_DIMENSION = 10_000;
const SIGNATURE_COORDINATE_MAX = 10_000;
const SIGNATURE_SAMPLE_DISTANCE = 4;
const MAX_CLICK_POINTS = 1_000;
const QUESTION_IMAGE_KEY = /^([A-Za-z0-9_-]{1,200})\/info-blocks\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:png|jpe?g|webp))$/i;

/** Whether a field type requires direct browser input and cannot be completed from speech. */
export function questionRequiresOnScreen(type: string): boolean {
  return ON_SCREEN_TYPES.has(type);
}

/** Allow only absolute HTTP(S) links without embedded credentials. */
export function isSafeQuestionLinkUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim() || value.length > 2_048) return false;
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:')
      && Boolean(url.hostname) && !url.username && !url.password;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

function isText(value: unknown, maxLength = MAX_TEXT_LENGTH): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength;
}

function isIdentifier(value: unknown): value is string {
  return isText(value, 200) && value === value.trim() && !/[<>\u0000-\u001F\u007F-\u009F]/.test(value);
}

function isNormalizedPoint(value: unknown): value is NormalizedPoint {
  return isRecord(value) && hasOnlyKeys(value, ['x', 'y'])
    && typeof value.x === 'number' && Number.isFinite(value.x) && value.x >= 0 && value.x <= 1
    && typeof value.y === 'number' && Number.isFinite(value.y) && value.y >= 0 && value.y <= 1;
}

function uniquePoints(points: readonly NormalizedPoint[]): boolean {
  return new Set(points.map((point) => `${point.x}:${point.y}`)).size === points.length;
}

function validTimezone(value: unknown): value is string {
  if (!isText(value, 100)) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function validDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day >= 1 && day <= daysInMonth[month - 1];
}

function validTime(value: string): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value);
}

/** Validate canonical local calendar values without depending on the host timezone. */
export function validDateTimeQuestionValue(mode: DateTimeQuestionConfig['mode'], value: string): boolean {
  if (mode === 'date') return validDate(value);
  if (mode === 'time') return validTime(value);
  const [date, time, extra] = value.split('T');
  return extra === undefined && Boolean(date && time && validDate(date) && validTime(time));
}

function validDateTimeQuestionBound(mode: DateTimeQuestionConfig['mode'], value: string): boolean {
  if (mode === 'date') return validDate(value);
  if (mode === 'time') return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
  const [date, time, extra] = value.split('T');
  return extra === undefined && Boolean(date && time && validDate(date)
    && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time));
}

/** Normalize exact mode-specific inclusive bounds for one date/time question. */
export function normalizeDateTimeQuestionConfig(value: unknown): DateTimeQuestionConfig | undefined {
  if (!isRecord(value) || !hasOnlyKeys(value, ['mode', 'min', 'max', 'rules'])
    || (value.mode !== 'date' && value.mode !== 'time' && value.mode !== 'date_time')) return undefined;
  if ((value.min !== undefined && (typeof value.min !== 'string' || !validDateTimeQuestionBound(value.mode, value.min)))
    || (value.max !== undefined && (typeof value.max !== 'string' || !validDateTimeQuestionBound(value.mode, value.max)))
    || (typeof value.min === 'string' && typeof value.max === 'string' && value.min > value.max)) return undefined;
  if (value.rules !== undefined && (value.mode === 'time' || !validDateRules(value.rules))) return undefined;
  return { mode: value.mode,
    ...(typeof value.min === 'string' ? { min: value.min } : {}),
    ...(typeof value.max === 'string' ? { max: value.max } : {}),
    ...(value.rules !== undefined ? { rules: value.rules as DateQuestionRules } : {}) };
}

/** Resolved calendar-day limits, safe to project without other answer values. */
export interface DateRuleBounds { min?: string; max?: string; unavailable: boolean }

/** Runtime facts come from the response authority, keyed by stable field identity. */
export interface DateRuleContext {
  now?: Date;
  answers?: Readonly<Record<string, unknown>>;
  /** Browser-only projection of server-resolved limits. Never accept from an answer. */
  bounds?: DateRuleBounds;
}

/** Reject malformed rules and offset-only zones absent from PostgreSQL's named timezone catalogue. */
function validDateRules(value: unknown): value is DateQuestionRules {
  if (!isRecord(value) || !hasOnlyKeys(value, ['timezone', 'allowedWeekdays', 'excludedDates', 'includedDates', 'min', 'max'])
    || !validTimezone(value.timezone) || /^[+-]/.test(value.timezone)) return false;
  if (value.allowedWeekdays !== undefined && (!Array.isArray(value.allowedWeekdays)
    || value.allowedWeekdays.length < 1 || value.allowedWeekdays.length > 7
    || value.allowedWeekdays.some(day => !Number.isInteger(day) || day < 0 || day > 6)
    || new Set(value.allowedWeekdays).size !== value.allowedWeekdays.length)) return false;
  for (const key of ['excludedDates', 'includedDates']) {
    const dates = value[key];
    if (dates !== undefined && (!Array.isArray(dates) || dates.length > 366
      || dates.some(day => typeof day !== 'string' || !validDate(day)) || new Set(dates).size !== dates.length)) return false;
  }
  for (const key of ['min', 'max']) {
    const bound = value[key];
    if (bound === undefined) continue;
    if (!isRecord(bound) || !Number.isInteger(bound.offsetDays) || Math.abs(Number(bound.offsetDays)) > 36500) return false;
    if (bound.source === 'today') { if (!hasOnlyKeys(bound, ['source', 'offsetDays'])) return false; }
    else if (bound.source !== 'answer' || !isIdentifier(bound.fieldId) || !hasOnlyKeys(bound, ['source', 'fieldId', 'offsetDays'])) return false;
  }
  const rules = value as unknown as DateQuestionRules;
  return !(rules.min && rules.max && rules.min.source === rules.max.source
    && (rules.min.source === 'today' || rules.max.source === 'answer' && rules.min.fieldId === rules.max.fieldId)
    && rules.min.offsetDays > rules.max.offsetDays);
}

/** Add calendar days without local-clock or daylight-saving arithmetic. */
function offsetDate(day: string, offset: number): string | undefined {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + offset);
  const result = date.toISOString().slice(0, 10);
  return validDate(result) ? result : undefined;
}

/** Resolve dynamic limits once against an explicit clock and accepted earlier answers. */
export function resolveDateRuleBounds(config: DateTimeQuestionConfig, context: DateRuleContext = {}): DateRuleBounds {
  if (!normalizeDateTimeQuestionConfig(config)) return { unavailable: true };
  if (context.bounds) return context.bounds;
  const rules = config.rules;
  if (!rules) return { unavailable: false };
  const resolve = (bound: DateRuleBound): string | undefined => {
    let day: string;
    if (bound.source === 'today') {
      const parts = new Intl.DateTimeFormat('en', { timeZone: rules.timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(context.now ?? new Date());
      const part = (type: string) => parts.find(item => item.type === type)?.value ?? '';
      day = `${part('year').padStart(4, '0')}-${part('month')}-${part('day')}`;
    } else {
      const answer = context.answers?.[bound.fieldId];
      if (!isRecord(answer) || typeof answer.value !== 'string') return undefined;
      day = answer.value.split('T')[0];
    }
    return validDate(day) ? offsetDate(day, bound.offsetDays) : undefined;
  };
  const min = rules.min ? resolve(rules.min) : undefined;
  const max = rules.max ? resolve(rules.max) : undefined;
  return { ...(min ? { min } : {}), ...(max ? { max } : {}),
    unavailable: Boolean(rules.min && !min || rules.max && !max || min && max && min > max) };
}

/** Date rules use the local calendar date, including on combined date/time questions. */
export function isDateQuestionDayAllowed(config: DateTimeQuestionConfig, day: string, context: DateRuleContext = {}): boolean {
  if (!validDate(day)) return false;
  const bounds = resolveDateRuleBounds(config, context);
  if (bounds.unavailable || bounds.min && day < bounds.min || bounds.max && day > bounds.max
    || config.min && day < config.min.slice(0, 10) || config.max && day > config.max.slice(0, 10)) return false;
  const rules = config.rules;
  if (rules?.excludedDates?.includes(day)) return false;
  return !rules?.allowedWeekdays || rules.includedDates?.includes(day) === true
    || rules.allowedWeekdays.includes(new Date(`${day}T12:00:00Z`).getUTCDay());
}

/** Whether a complete local value has the configured format and inclusive range. */
export function isDateTimeQuestionValueInRange(config: DateTimeQuestionConfig, value: string, context: DateRuleContext = {}): boolean {
  const normalized = normalizeDateTimeQuestionConfig(config);
  if (!normalized || !validDateTimeQuestionValue(normalized.mode, value)) return false;
  if (normalized.mode !== 'time' && !isDateQuestionDayAllowed(normalized, value.slice(0, 10), context)) return false;
  const comparable = normalized.mode === 'date' || (value.length !== 5 && value.length !== 16)
    ? value : `${value}:00`;
  const minimum = normalized.min === undefined || normalized.mode === 'date'
    ? normalized.min : `${normalized.min}:00`;
  const maximum = normalized.max === undefined || normalized.mode === 'date'
    ? normalized.max : `${normalized.max}:00`;
  return (minimum === undefined || comparable >= minimum)
    && (maximum === undefined || comparable <= maximum);
}

/** Validate a complete date/time answer envelope against authored bounds. */
export function validateDateTimeQuestionAnswer(
  config: DateTimeQuestionConfig,
  value: unknown,
  context: DateRuleContext = {},
): QuestionAnswerValidation {
  if (!isRecord(value) || !hasOnlyKeys(value, ['value', 'timezone']) || typeof value.value !== 'string') return { valid: false };
  const normalized = normalizeDateTimeQuestionConfig(config);
  if (!normalized || !isDateTimeQuestionValueInRange(normalized, value.value, context)) return { valid: false };
  const dateTime = value as unknown as DateTimeAnswerValue;
  if ((normalized.mode === 'date' || normalized.mode === 'time') && dateTime.timezone !== undefined) return { valid: false };
  if (normalized.mode === 'date_time' && !validTimezone(dateTime.timezone)) return { valid: false };
  return { valid: true, value: dateTime };
}

function validateDateTime(field: Extract<FormField, { type: 'date_time' }>, value: unknown, context: DateRuleContext): QuestionAnswerValidation {
  const rawConfig: unknown = (field as unknown as Record<string, unknown>).dateTime;
  return validateDateTimeQuestionAnswer(rawConfig as DateTimeQuestionConfig, value, context);
}

function validateOptionIds(field: Extract<FormField, { type: 'image_choice' | 'ranking' }>, value: unknown): QuestionAnswerValidation {
  if (!Array.isArray(value) || !value.every(isIdentifier) || new Set(value).size !== value.length) return { valid: false };
  const rawOptions: unknown = (field as unknown as Record<string, unknown>).options;
  if (!Array.isArray(rawOptions) || rawOptions.length === 0
    || rawOptions.some(option => !isRecord(option) || !isIdentifier(option.id) || !isText(option.label, 1_000))) return { valid: false };
  const optionIds = rawOptions.map((option) => String((option as Record<string, unknown>).id));
  const configured = new Set(optionIds);
  if (configured.size !== optionIds.length) return { valid: false };
  if (value.some((id) => !configured.has(id))) return { valid: false };
  if (field.type === 'ranking') {
    return value.length === configured.size ? { valid: true, value } : { valid: false };
  }
  const selectionMode: unknown = (field as unknown as Record<string, unknown>).selectionMode;
  if (selectionMode !== 'single' && selectionMode !== 'multiple') return { valid: false };
  const minimum = typeof field.minSelected === 'number' ? field.minSelected : 1;
  const maximum = selectionMode === 'single' ? 1 : typeof field.maxSelected === 'number' ? field.maxSelected : rawOptions.length;
  if (!Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 1 || maximum < minimum
    || maximum > rawOptions.length || (selectionMode === 'single' && (minimum !== 1 || maximum !== 1))) return { valid: false };
  if (value.length < minimum || value.length > maximum) return { valid: false };
  return { valid: true, value };
}

function validateMatrix(field: Extract<FormField, { type: 'matrix' }>, value: unknown): QuestionAnswerValidation {
  if (!isRecord(value) || !hasOnlyKeys(value, ['selections']) || !isRecord(value.selections)) return { valid: false };
  const rawMatrix: unknown = (field as unknown as Record<string, unknown>).matrix;
  if (!isRecord(rawMatrix) || !Array.isArray(rawMatrix.rows) || !Array.isArray(rawMatrix.columns)
    || rawMatrix.rows.length === 0 || rawMatrix.columns.length === 0
    || rawMatrix.rows.some(item => !isRecord(item) || !isIdentifier(item.id) || !isText(item.label, 1_000))
    || rawMatrix.columns.some(item => !isRecord(item) || !isIdentifier(item.id) || !isText(item.label, 1_000))) return { valid: false };
  const selections = value.selections;
  const rowIds = rawMatrix.rows.map(item => String((item as Record<string, unknown>).id));
  const rawColumnIds = rawMatrix.columns.map(item => String((item as Record<string, unknown>).id));
  const columnIds = new Set(rawColumnIds);
  if (new Set(rowIds).size !== rowIds.length || columnIds.size !== rawColumnIds.length) return { valid: false };
  const mode = field.selectionMode === undefined ? 'single' : field.selectionMode;
  if (mode !== 'single' && mode !== 'multiple') return { valid: false };
  if (Object.keys(selections).length !== rowIds.length || rowIds.some(rowId => {
    const selected = selections[rowId];
    if (mode === 'single') return typeof selected !== 'string' || !columnIds.has(selected);
    return !Array.isArray(selected) || selected.length === 0 || selected.length > columnIds.size
      || new Set(selected).size !== selected.length || selected.some(id => typeof id !== 'string' || !columnIds.has(id));
  })) return { valid: false };
  if (Object.keys(selections).some((rowId) => !rowIds.includes(rowId))) return { valid: false };
  return { valid: true, value: value as unknown as MatrixAnswerValue };
}

function validateAddress(field: Extract<FormField, { type: 'address' }>, value: unknown): QuestionAnswerValidation {
  if (!isRecord(value) || !hasOnlyKeys(value, ['line1', 'line2', 'city', 'region', 'postalCode', 'country'])) return { valid: false };
  const rawConfig: unknown = (field as unknown as Record<string, unknown>).address;
  if (!isRecord(rawConfig) || !hasOnlyKeys(rawConfig, ['requirePostalCode']) || typeof rawConfig.requirePostalCode !== 'boolean') return { valid: false };
  if (!isText(value.line1) || !isText(value.city) || !isText(value.country, 200)) return { valid: false };
  for (const key of ['line2', 'region', 'postalCode'] as const) {
    if (value[key] !== undefined && !isText(value[key])) return { valid: false };
  }
  if (rawConfig.requirePostalCode && !isText(value.postalCode)) return { valid: false };
  return { valid: true, value: value as unknown as AddressAnswerValue };
}

function isSignaturePoint(value: unknown): value is SignaturePoint {
  return Array.isArray(value) && value.length === 2
    && value.every(coordinate => typeof coordinate === 'number' && Number.isInteger(coordinate)
      && coordinate >= 0 && coordinate <= SIGNATURE_COORDINATE_MAX);
}

function isReadableSignatureName(value: unknown): value is string {
  return isText(value, 500)
    && !/[\u0000-\u001F\u007F-\u009F]/u.test(value)
    && value.replace(/[\s\p{Cf}]/gu, '').length > 0;
}

function compactSignatureStroke(points: readonly SignaturePoint[]): SignaturePoint[] {
  const compacted: SignaturePoint[] = [];
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    const previous = compacted.at(-1);
    if (previous?.[0] === point[0] && previous[1] === point[1]) continue;
    const isEndpoint = index === points.length - 1;
    if (!previous || isEndpoint
      || ((point[0] - previous[0]) ** 2 + (point[1] - previous[1]) ** 2) >= SIGNATURE_SAMPLE_DISTANCE ** 2) {
      compacted.push([point[0], point[1]]);
    }
  }
  return compacted;
}

function withinSignatureByteLimit(value: ReadableSignatureAnswerValue): boolean {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength <= MAX_SIGNATURE_BYTES;
}

/** Count all canonical points in a drawn signature. */
export function signaturePointCount(value: ReadableSignatureAnswerValue | undefined): number {
  return value?.kind === 'drawn'
    ? value.strokes.reduce((total, stroke) => total + stroke.length, 0)
    : 0;
}

/** Normalize readable legacy or v2 signature data into the canonical v2 representation. */
export function normalizeSignatureAnswer(value: unknown): ReadableSignatureAnswerValue | undefined {
  if (!isRecord(value)) return undefined;
  if (value.kind === 'typed') {
    const allowedKeys = value.version === 2 ? ['version', 'kind', 'typedName'] : ['kind', 'typedName'];
    if (!hasOnlyKeys(value, allowedKeys) || (value.version !== undefined && value.version !== 2)
      || !isReadableSignatureName(value.typedName)) return undefined;
    const answer: SignatureAnswerValue = { version: 2, kind: 'typed', typedName: value.typedName.trim() };
    return withinSignatureByteLimit(answer) ? answer : undefined;
  }
  const versioned = value.version === 2;
  const allowedKeys = versioned ? ['version', 'kind', 'signerName', 'encoding', 'canvas', 'strokes'] : ['kind', 'strokes'];
  if (value.kind !== 'drawn' || !hasOnlyKeys(value, allowedKeys) || !Array.isArray(value.strokes)
    || value.strokes.length === 0 || value.strokes.length > MAX_SIGNATURE_STROKES
    || (value.signerName !== undefined && !isReadableSignatureName(value.signerName))) return undefined;

  let canvas = { width: 4, height: 1 };
  const strokes: SignaturePoint[][] = [];
  let rawPointCount = 0;
  if (versioned) {
    if (value.encoding !== 'normalized-10000' || !isRecord(value.canvas)
      || !hasOnlyKeys(value.canvas, ['width', 'height'])) return undefined;
    const { width, height } = value.canvas;
    if (typeof width !== 'number' || typeof height !== 'number' || !Number.isInteger(width) || !Number.isInteger(height)
      || width < 1 || height < 1 || width > MAX_SIGNATURE_CANVAS_DIMENSION || height > MAX_SIGNATURE_CANVAS_DIMENSION
      || width / height < 1 || width / height > 8) return undefined;
    canvas = { width, height };
    for (const stroke of value.strokes) {
      if (!Array.isArray(stroke) || stroke.length === 0 || !stroke.every(isSignaturePoint)) return undefined;
      rawPointCount += stroke.length;
      if (rawPointCount > MAX_SIGNATURE_POINTS) return undefined;
      strokes.push(compactSignatureStroke(stroke));
    }
  } else {
    for (const stroke of value.strokes) {
      if (!isRecord(stroke) || !hasOnlyKeys(stroke, ['points']) || !Array.isArray(stroke.points)
        || stroke.points.length === 0 || !stroke.points.every(isNormalizedPoint)) return undefined;
      rawPointCount += stroke.points.length;
      if (rawPointCount > MAX_SIGNATURE_POINTS) return undefined;
      strokes.push(compactSignatureStroke(stroke.points.map(point => [
        Math.round(point.x * SIGNATURE_COORDINATE_MAX),
        Math.round(point.y * SIGNATURE_COORDINATE_MAX),
      ])));
    }
  }
  const answer: ReadableSignatureAnswerValue = { version: 2, kind: 'drawn',
    ...(typeof value.signerName === 'string' ? { signerName: value.signerName.trim() } : {}),
    encoding: 'normalized-10000', canvas, strokes };
  return withinSignatureByteLimit(answer) ? answer : undefined;
}

/** Return the persisted written name for either signature method. */
export function signatureSignerName(value: unknown): string | undefined {
  const normalized = normalizeSignatureAnswer(value);
  return normalized?.kind === 'typed' ? normalized.typedName : normalized?.signerName;
}

export type SignatureSubmissionValidation =
  | { valid: true; value: SignatureAnswerValue }
  | { valid: false };

/** Validate a newly submitted signature, including a written name for drawings. */
export function validateSignatureSubmission(value: unknown): SignatureSubmissionValidation {
  const normalized = normalizeSignatureAnswer(value);
  if (!normalized || (normalized.kind === 'drawn' && normalized.signerName === undefined)) return { valid: false };
  return { valid: true, value: normalized as SignatureAnswerValue };
}

/** Return the aspect-preserving SVG view box for a canonical drawn signature. */
export function signatureCanvasViewBox(value: Extract<ReadableSignatureAnswerValue, { kind: 'drawn' }>): string {
  return `0 0 ${value.canvas.width} ${value.canvas.height}`;
}

function signatureSvgCoordinate(coordinate: number, dimension: number): number {
  return Number(((coordinate / SIGNATURE_COORDINATE_MAX) * dimension).toFixed(2));
}

/** Convert a compact stroke into a smooth, deterministic SVG path. */
export function signatureStrokePath(value: Extract<ReadableSignatureAnswerValue, { kind: 'drawn' }>, stroke: SignaturePoint[]): string {
  const points = stroke.map(point => [
    signatureSvgCoordinate(point[0], value.canvas.width),
    signatureSvgCoordinate(point[1], value.canvas.height),
  ] as const);
  const first = points[0];
  if (!first) return '';
  if (points.length === 1) return `M ${first[0]} ${first[1]} l 0.01 0`;
  if (points.length === 2) return `M ${first[0]} ${first[1]} L ${points[1][0]} ${points[1][1]}`;
  let path = `M ${first[0]} ${first[1]}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const point = points[index];
    const next = points[index + 1];
    path += ` Q ${point[0]} ${point[1]} ${Number(((point[0] + next[0]) / 2).toFixed(2))} ${Number(((point[1] + next[1]) / 2).toFixed(2))}`;
  }
  const last = points.at(-1)!;
  return `${path} L ${last[0]} ${last[1]}`;
}

function validateSignature(value: unknown): QuestionAnswerValidation {
  const normalized = normalizeSignatureAnswer(value);
  return normalized ? { valid: true, value: normalized } : { valid: false };
}

function validateClickMap(field: Extract<FormField, { type: 'image_click_map' }>, value: unknown): QuestionAnswerValidation {
  if (!isRecord(value) || !hasOnlyKeys(value, ['points']) || !Array.isArray(value.points)
    || value.points.length > MAX_CLICK_POINTS || !value.points.every(isNormalizedPoint) || !uniquePoints(value.points)) return { valid: false };
  const minimum = typeof field.minClicks === 'number' ? field.minClicks : 1;
  const maximum = typeof field.maxClicks === 'number' ? field.maxClicks : 1;
  if (!Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 1 || maximum < minimum || maximum > MAX_CLICK_POINTS) return { valid: false };
  return value.points.length >= minimum && value.points.length <= maximum
    ? { valid: true, value: value as unknown as ImageClickMapAnswerValue }
    : { valid: false };
}

/** Validate a structured answer using the exact snapshotted field configuration. */
export function validateQuestionStructuredValue(field: FormField, value: unknown, context: DateRuleContext = {}): QuestionAnswerValidation {
  switch (field.type) {
    case 'checkbox':
      return typeof value === 'boolean' && (!field.required || value) ? { valid: true, value } : { valid: false };
    case 'image_choice':
    case 'ranking':
      return validateOptionIds(field, value);
    case 'date_time':
      return validateDateTime(field, value, context);
    case 'matrix':
      return validateMatrix(field, value);
    case 'address':
      return validateAddress(field, value);
    case 'signature':
      return validateSignature(value);
    case 'image_click_map':
      return validateClickMap(field, value);
    default:
      return { valid: false };
  }
}

/** Format one validated structured or full-name answer without falling back to object coercion. */
export function formatQuestionAnswer(field: FormField, value: unknown): string | undefined {
  if (field.type === 'name') {
    // Plain strings predate the versioned answer and are shown as stored; objects must match the strict contract.
    if (typeof value === 'string') return value.trim() ? value : undefined;
    const answer = normalizeFullName(value);
    return answer ? formatNameAnswer(answer) : undefined;
  }
  const validated = validateQuestionStructuredValue(field.type === 'date_time'
    ? { ...field, dateTime: { ...field.dateTime, rules: undefined } } : field, value);
  if (!validated.valid) return undefined;
  switch (field.type) {
    case 'checkbox': return validated.value ? 'Checked' : 'Not checked';
    case 'image_choice':
    case 'ranking': {
      const ids = validated.value as string[];
      return ids.map((id) => field.options.find((option) => option.id === id)?.label).filter(Boolean).join(', ');
    }
    case 'date_time': {
      const answer = validated.value as DateTimeAnswerValue;
      return answer.timezone ? `${answer.value} (${answer.timezone})` : answer.value;
    }
    case 'matrix': {
      const answer = validated.value as MatrixAnswerValue;
      const columns = new Map(field.matrix.columns.map(column => [column.id, column.label]));
      const rows = field.matrix.rows.map(row => {
        const selected = answer.selections[row.id];
        const ids = Array.isArray(selected) ? selected : [selected];
        return { label: row.label, labels: ids.map(id => columns.get(id) ?? '') };
      });
      // Repeated long column labels can exceed the public state display budget.
      // Keep the complete structured value and show an explicit summary instead.
      const displayLength = rows.reduce((total, row) => total + row.label.length + 4
        + row.labels.reduce((length, label) => length + label.length + 2, 0), 0);
      if (displayLength > 1_000_000) {
        const count = rows.reduce((total, row) => total + row.labels.length, 0);
        return `${rows.length} rows answered (${count} selections)`;
      }
      return rows.map(row => `${row.label}: ${row.labels.join(', ')}`).join(', ');
    }
    case 'address': {
      const answer = validated.value as AddressAnswerValue;
      return [answer.line1, answer.line2, answer.city, answer.region, answer.postalCode, answer.country].filter(Boolean).join(', ');
    }
    case 'signature': {
      return signatureSignerName(validated.value) ?? 'Drawn signature';
    }
    case 'image_click_map': {
      const count = (validated.value as ImageClickMapAnswerValue).points.length;
      return `${count} point${count === 1 ? '' : 's'}`;
    }
    default: return undefined;
  }
}

/** Validate a visual-question image before it can be published. */
export function validateQuestionImage(value: unknown, expectedWorkspaceId?: string): QuestionImageIssue[] {
  if (!isRecord(value)) return ['missing_image', 'missing_alt'];
  const issues: QuestionImageIssue[] = [];
  const imageStorageKey = isText(value.imageStorageKey, 2_000) ? value.imageStorageKey : null;
  const imageUrl = isText(value.imageUrl, 2_048) ? value.imageUrl : null;
  const hasStorageKey = imageStorageKey !== null;
  const hasImageUrl = imageUrl !== null;
  if (!hasStorageKey || !hasImageUrl) issues.push('missing_image');
  if (imageStorageKey && imageUrl) {
    const match = QUESTION_IMAGE_KEY.exec(imageStorageKey);
    const expectedUrl = match ? `/api/workspace-assets/${match[1]}/info-blocks/${match[2]}` : null;
    if (!match || imageUrl !== expectedUrl || (expectedWorkspaceId !== undefined && match[1] !== expectedWorkspaceId)) {
      issues.push('invalid_image_url');
    }
  }
  if (!isText(value.alt, 1_000)) issues.push('missing_alt');
  if (!hasOnlyKeys(value, ['imageUrl', 'imageStorageKey', 'alt', 'caption'])
    || (value.caption !== undefined && !isText(value.caption, 2_000))) issues.push('invalid_image_metadata');
  return issues;
}
